// -- [Demo signup modal – opens from "Book a demo", pricing, or header; draft in localStorage + URL prefill]

import React, { useState, useCallback, useRef, useEffect, useMemo } from 'react'
import * as Dialog from '@radix-ui/react-dialog'
import { getInitialFormState, setStoredDraft, clearStoredDraft, setSubmittedDemoEmail, setLastSubmission, getLastSubmission, clearLastSubmission } from '@/lib/signupDraft'
import { getAttribution } from '@/lib/attribution'
import { DEALERSHIP_SUGGESTIONS } from '@/data/dealershipSuggestions'
import { filterAndSortDealerships } from '@/lib/dealershipSearch'

const SIGNUP_INTRO =
"Drivible is a risk free way to turbo charge your dealerships productivity by using our AI-powered document storage and processing solutions. Fill in the form below and we can have your dealership storing and searching your documents using your current scanners in no time."

const DISCLAIMER =
  'BY SUBMITTING THIS FORM, YOU ACCEPT THAT YOU ARE AUTHORISED BY YOUR BUSINESS OR EMPLOYER TO SETUP AN ACCOUNT.'

// -- [Interest slider: 5 levels from "Just curious" to "Get me onboarded NOW"; each level sets a preset of options]
const INTEREST_LEVELS: ReadonlyArray<readonly [string, readonly boolean[]]> = [
  ['Just curious', [true, false, false, false, false]],
  ['Interested', [true, true, false, false, false]],
  ['Ready to try', [true, true, true, false, false]],
  ['Ready for a demo', [true, true, true, true, false]],
  ['Get me onboarded NOW', [true, true, true, true, true]],
] as const

const INTEREST_OPTION_LABELS = [
  'Send me a link to some videos and info so I can learn more',
  'Sign me up to the newsletter',
  'Create a trial account for me',
  'Schedule a demo',
  'Send someone to train our staff',
] as const

export interface SignupInterestOptions {
  sendVideosAndInfo: boolean
  signUpNewsletter: boolean
  createAccount: boolean
  scheduleDemo: boolean
  trainStaff: boolean
}

export interface SignupPayload {
  dealership: string
  website?: string
  firstName: string
  lastName: string
  email: string
  mobile?: string
  /** -- Buttons that opened the modal (e.g. "Book a demo", "Pricing - Starter - Trial now") */
  clickedButtons?: string[]
  /** -- "Just curious" | "Interested" | … | "Get me onboarded NOW" | "Custom" */
  interestLevelLabel?: string
  interestOptions?: SignupInterestOptions
  /** -- Anti-spam; frontend sends 27 */
  verificationCode: number
  /** -- Hidden: current role key at submit time */
  roleKey?: string
  /** -- Hidden: current topic key at submit time */
  topicKey?: string
  /** -- Hidden: attribution (referrer, initialUrl, UTM-*, ipAddress) for tracking/attribution */
  attribution?: Record<string, string>
}

/** -- Submission request adds hmac and ttl from HMAC endpoint response */
export interface SignupSubmissionPayload extends SignupPayload {
  hmac: string
  ttl: number
}

export interface SignupHmacResponse {
  hmac: string
  ttl: number
}

// -- [Submission API URLs: prefer env vars, else derive from VITE_PUBLIC_MARKETING_API_URL base]
function getSubmissionApiBase(): string | null {
  const hmacUrl = import.meta.env.VITE_PUBLIC_MARKETING_SUBMISSION_HMAC_API_URL
  const subUrl = import.meta.env.VITE_PUBLIC_MARKETING_SUBMISSION_API_URL
  if (hmacUrl?.trim() || subUrl?.trim()) {
    if (hmacUrl?.trim()) {
      try {
        const u = new URL(hmacUrl.trim())
        return u.origin
      } catch {
        // fall through
      }
    }
    if (subUrl?.trim()) {
      try {
        const u = new URL(subUrl.trim())
        return u.origin
      } catch {
        // fall through
      }
    }
  }
  const marketingUrl = import.meta.env.VITE_PUBLIC_MARKETING_API_URL
  if (!marketingUrl?.trim()) return null
  try {
    const u = new URL(marketingUrl.trim())
    return u.origin
  } catch {
    return null
  }
}

function getSubmissionHmacApiUrl(): string {
  const env = import.meta.env.VITE_PUBLIC_MARKETING_SUBMISSION_HMAC_API_URL
  if (env && typeof env === 'string' && env.trim()) return env.trim()
  const base = getSubmissionApiBase()
  if (base) return `${base}/v1/public-marketing/submission-form-hmac`
  return ''
}

function getSubmissionApiUrl(): string {
  const env = import.meta.env.VITE_PUBLIC_MARKETING_SUBMISSION_API_URL
  if (env && typeof env === 'string' && env.trim()) return env.trim()
  const base = getSubmissionApiBase()
  if (base) return `${base}/v1/public-marketing/submission-form`
  return ''
}

function validateEmail(value: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value)
}

function validateUrl(value: string): boolean {
  if (!value || value === 'http://' || value === 'https://') return true
  try {
    new URL(value.startsWith('http') ? value : `https://${value}`)
    return true
  } catch {
    return false
  }
}

/** -- Default interest level from the most recently clicked button. Trial Now → Ready to try (2); most others → Ready for a demo (3). */
function getDefaultInterestFromButtons(clickedButtons: string[]): readonly boolean[] {
  if (clickedButtons.length === 0) return INTEREST_LEVELS[0][1]
  const lastLabel = clickedButtons[clickedButtons.length - 1].toLowerCase()
  if (lastLabel.includes('starter') && lastLabel.includes('trial')) return INTEREST_LEVELS[2][1]
  // -- Book a demo, Setup Now, Contact Us, Benchmark, and all other buttons → level 3 (Ready for a demo)
  return INTEREST_LEVELS[3][1]
}

/** -- Extract main domain from website URL for email prefill (e.g. https://www.jarvistoyota.com.au/ → jarvistoyota.com.au). */
function extractEmailDomain(website: string): string | null {
  const raw = website.trim()
  if (!raw || raw === 'http://' || raw === 'https://') return null
  try {
    const url = new URL(raw.startsWith('http') ? raw : `https://${raw}`)
    let host = url.hostname
    if (host.startsWith('www.')) host = host.slice(4)
    return host || null
  } catch {
    return null
  }
}

/** -- Australia-only: normalize to national digits (strip +61, leading 0, non-digits). E.g. 0493088319 or +61493088319 → 493088319 */
function normalizeAustralianMobile(value: string): string {
  const digits = value.replace(/\D/g, '')
  if (digits.startsWith('61') && digits.length > 9) return digits.slice(2)
  if (digits.startsWith('0')) return digits.slice(1)
  return digits
}

interface DemoSignupModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  /** -- Buttons that opened the modal this session (hidden from user, sent in payload) */
  clickedButtons?: string[]
  /** -- Current role key (hidden field in payload) */
  roleKey?: string
  /** -- Current topic key (hidden field in payload) */
  topicKey?: string
  onSuccess?: () => void
}

function getInitialState() {
  const init = getInitialFormState()
  return init
}

export function DemoSignupModal({
  open,
  onOpenChange,
  clickedButtons = [],
  roleKey: roleKeyProp,
  topicKey: topicKeyProp,
  onSuccess,
}: DemoSignupModalProps) {
  const [initial] = useState(getInitialState)
  const [dealership, setDealership] = useState(initial.dealership)
  const [website, setWebsite] = useState(initial.website)
  const [firstName, setFirstName] = useState(initial.firstName)
  const [lastName, setLastName] = useState(initial.lastName)
  const [email, setEmail] = useState(initial.email)
  const [mobile, setMobile] = useState(() => normalizeAustralianMobile(initial.mobile ?? ''))
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [status, setStatus] = useState<'idle' | 'submitting' | 'success' | 'error'>('idle')
  const [submitError, setSubmitError] = useState<React.ReactNode | null>(null)
  const [submitProgress, setSubmitProgress] = useState<0 | 50 | 100>(0)
  // -- [After error: button shows red for 3s then fades back to normal over 2s; error message stays until next attempt]
  const [buttonErrorHighlight, setButtonErrorHighlight] = useState(false)
  // -- [Honeypot: must stay empty; bots often fill all fields; never sent to API]
  const [companyWebsiteConfirm, setCompanyWebsiteConfirm] = useState('')
  // -- [Interest: 5 booleans; slider sets presets, checkboxes can override → "Custom" if no preset matches]
  const [interestOptions, setInterestOptions] = useState<readonly boolean[]>(
    () => INTEREST_LEVELS[0][1]
  )
  const interestOptionsArray = [...interestOptions]
  // -- [Dealership autocomplete: suggestions from CSV; optional geolocation for distance ordering]
  const [dealershipSuggestionsOpen, setDealershipSuggestionsOpen] = useState(false)
  const [userLocation, setUserLocation] = useState<{ lat: number; lng: number } | null>(null)
  const dealershipInputRef = useRef<HTMLInputElement>(null)
  const dealershipListRef = useRef<HTMLUListElement>(null)
  const closeSuggestionsTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  // -- [Once user touches slider or a checkbox, we stop applying button-based defaults until modal is closed]
  const userHasTouchedInterestRef = useRef(false)
  // -- [Location permission notice: asking → granted/denied → slide away]
  const [locationNoticeState, setLocationNoticeState] = useState<'off' | 'asking' | 'granted' | 'denied'>('off')
  const [locationNoticeExiting, setLocationNoticeExiting] = useState<'up' | 'right' | null>(null)
  const locationNoticeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const locationRequestDelayRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // -- [Email domain prefill from website: e.g. https://www.jarvistoyota.com.au/ → @jarvistoyota.com.au]
  const suggestedEmailDomain = useMemo(() => extractEmailDomain(website), [website])

  // -- Resolve current selection to a preset index or null (Custom)
  const interestPresetIndex: number | null = (() => {
    for (let i = 0; i < INTEREST_LEVELS.length; i++) {
      const preset = INTEREST_LEVELS[i][1]
      if (preset.every((v, j) => v === interestOptions[j])) return i
    }
    return null
  })()
  const interestLevelLabel =
    interestPresetIndex !== null
      ? INTEREST_LEVELS[interestPresetIndex][0]
      : 'Custom'
  // -- Slider thumb position: preset index, or (when Custom) approximate by number of options checked
  const interestSliderValue =
    interestPresetIndex !== null
      ? interestPresetIndex
      : Math.min(
          Math.max(0, interestOptions.filter(Boolean).length - 1),
          INTEREST_LEVELS.length - 1
        )

  const setInterestFromSlider = useCallback((index: number) => {
    userHasTouchedInterestRef.current = true
    setInterestOptions(INTEREST_LEVELS[index][1])
  }, [])

  const setInterestOption = useCallback((optionIndex: number, checked: boolean) => {
    userHasTouchedInterestRef.current = true
    setInterestOptions((prev) => {
      const next = [...prev]
      next[optionIndex] = checked
      return next
    })
  }, [])

  const openDealershipSuggestions = useCallback(() => {
    if (closeSuggestionsTimerRef.current) {
      clearTimeout(closeSuggestionsTimerRef.current)
      closeSuggestionsTimerRef.current = null
    }
    setDealershipSuggestionsOpen(true)
  }, [])

  const closeDealershipSuggestions = useCallback(() => {
    closeSuggestionsTimerRef.current = setTimeout(() => {
      setDealershipSuggestionsOpen(false)
      closeSuggestionsTimerRef.current = null
    }, 150)
  }, [])

  const selectDealership = useCallback(
    (name: string, url?: string) => {
      setDealership(name)
      if (url) setWebsite(url.startsWith('http') ? url : `https://${url}`)
      setDealershipSuggestionsOpen(false)
      dealershipInputRef.current?.blur()
    },
    []
  )

  const didSuccessRef = useRef(false)
  const autoCloseTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  // -- [Radix Dialog can leave pointer-events: none on body after close; restore so Role/Topic selects work]
  useEffect(() => {
    if (open) return
    const id = setTimeout(() => {
      document.body.style.pointerEvents = ''
    }, 0)
    return () => clearTimeout(id)
  }, [open])

  // -- [Apply interest default from the last clicked button only when opening; once the user changes the slider or any checkbox we never override (even after close/reopen) until the form is reset after success]
  useEffect(() => {
    if (!open) return
    if (userHasTouchedInterestRef.current) return
    if (clickedButtons.length === 0) return
    const newDefault = getDefaultInterestFromButtons(clickedButtons)
    setInterestOptions(newDefault)
  }, [open, clickedButtons])

  // -- [Location notice text for reading-time calculation: ~1s per 15 chars + 2s extra]
  const LOCATION_ASK_MESSAGE =
    "We're requesting your location so we can sort dealership results by distance. Allow or block in your browser."
  const LOCATION_READ_MS = Math.max(4000, LOCATION_ASK_MESSAGE.length * 45)
  const LOCATION_VISIBLE_EXTRA_MS = 2000
  const LOCATION_AUTO_DISMISS_MS = LOCATION_READ_MS + LOCATION_VISIBLE_EXTRA_MS

  // -- [Show location notice first, then request geolocation after a short delay]
  useEffect(() => {
    if (!open) {
      setLocationNoticeState('off')
      setLocationNoticeExiting(null)
      if (locationNoticeTimerRef.current) {
        clearTimeout(locationNoticeTimerRef.current)
        locationNoticeTimerRef.current = null
      }
      if (locationRequestDelayRef.current) {
        clearTimeout(locationRequestDelayRef.current)
        locationRequestDelayRef.current = null
      }
      return
    }
    if (userLocation || !navigator.geolocation?.getCurrentPosition) return

    setLocationNoticeState('asking')

    const clearTimers = () => {
      if (locationNoticeTimerRef.current) {
        clearTimeout(locationNoticeTimerRef.current)
        locationNoticeTimerRef.current = null
      }
      if (locationRequestDelayRef.current) {
        clearTimeout(locationRequestDelayRef.current)
        locationRequestDelayRef.current = null
      }
    }

    locationRequestDelayRef.current = setTimeout(() => {
      locationRequestDelayRef.current = null
      navigator.geolocation.getCurrentPosition(
        (pos) => {
          clearTimers()
          setUserLocation({ lat: pos.coords.latitude, lng: pos.coords.longitude })
          setLocationNoticeState('granted')
          locationNoticeTimerRef.current = setTimeout(() => {
            locationNoticeTimerRef.current = null
            setLocationNoticeExiting('up')
            setTimeout(() => {
              setLocationNoticeState('off')
              setLocationNoticeExiting(null)
            }, 400)
          }, 8000)
        },
        () => {
          clearTimers()
          setLocationNoticeState('denied')
          locationNoticeTimerRef.current = setTimeout(() => {
            locationNoticeTimerRef.current = null
            setLocationNoticeExiting('right')
            setTimeout(() => {
              setLocationNoticeState('off')
              setLocationNoticeExiting(null)
            }, 400)
          }, 6000)
        },
        { enableHighAccuracy: false, timeout: 8000, maximumAge: 300000 }
      )
    }, 500)

    locationNoticeTimerRef.current = setTimeout(() => {
      locationNoticeTimerRef.current = null
      setLocationNoticeExiting('up')
      setTimeout(() => {
        setLocationNoticeState('off')
        setLocationNoticeExiting(null)
      }, 400)
    }, LOCATION_AUTO_DISMISS_MS)

    return () => {
      clearTimers()
    }
  }, [open, userLocation])

  const dealershipSuggestions = filterAndSortDealerships(
    DEALERSHIP_SUGGESTIONS,
    dealership,
    userLocation?.lat,
    userLocation?.lng
  )
  const showDealershipList =
    dealershipSuggestionsOpen && dealership.trim().length > 0 && dealershipSuggestions.length > 0

  // -- Persist draft to localStorage when fields change; skip when showing success
  useEffect(() => {
    if (status === 'success') return
    setStoredDraft({
      dealership,
      website,
      firstName,
      lastName,
      email,
      mobile,
    })
  }, [dealership, website, firstName, lastName, email, mobile, status])

  const resetForm = useCallback(() => {
    setDealership('')
    setWebsite('')
    setFirstName('')
    setLastName('')
    setEmail('')
    setMobile('')
    setCompanyWebsiteConfirm('')
    setInterestOptions(INTEREST_LEVELS[0][1])
    userHasTouchedInterestRef.current = false
    setErrors({})
    setStatus('idle')
    setSubmitError(null)
    setSubmitProgress(0)
    clearStoredDraft()
  }, [])

  const handleOpenChange = useCallback(
    (next: boolean) => {
      onOpenChange(next)
    },
    [onOpenChange]
  )

  // -- [When opening modal: if we have a stored last submission, show success view with that data]
  useEffect(() => {
    if (!open) return
    const last = getLastSubmission()
    if (!last) return
    setDealership(last.dealership)
    setFirstName(last.firstName)
    setLastName(last.lastName)
    setEmail(last.email)
    setMobile(last.mobile)
    setInterestOptions(last.interestOptions)
    setStatus('success')
  }, [open])

  const handleSubmitAnother = useCallback(() => {
    clearLastSubmission()
    resetForm()
  }, [resetForm])

  // -- [10s auto-close after successful submit]
  useEffect(() => {
    if (!open || status !== 'success') return
    autoCloseTimerRef.current = setTimeout(() => {
      autoCloseTimerRef.current = null
      onOpenChange(false)
    }, 10_000)
    return () => {
      if (autoCloseTimerRef.current != null) {
        clearTimeout(autoCloseTimerRef.current)
        autoCloseTimerRef.current = null
      }
    }
  }, [open, status, onOpenChange])

  // -- [Error: show button in red for 3s then fade back to normal over 2s; error message stays above until next attempt]
  useEffect(() => {
    if (status === 'submitting') {
      setButtonErrorHighlight(false)
      return
    }
    if (status === 'error') {
      setButtonErrorHighlight(true)
      const t = setTimeout(() => setButtonErrorHighlight(false), 3000)
      return () => clearTimeout(t)
    }
    return undefined
  }, [status])

  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault()
      // -- [Honeypot: if filled, treat as bot; do not submit or show error]
      if (companyWebsiteConfirm.trim() !== '') return

      const nextErrors: Record<string, string> = {}
      if (!dealership.trim()) nextErrors.dealership = 'Dealership is required'
      if (!firstName.trim()) nextErrors.firstName = 'First name is required'
      if (!lastName.trim()) nextErrors.lastName = 'Last name is required'
      if (!email.trim()) {
        nextErrors.email = 'Email is required'
      } else if (!validateEmail(email)) {
        nextErrors.email = 'Please enter a valid email address'
      }
      const websiteVal = website.trim()
      if (websiteVal && !validateUrl(websiteVal)) {
        nextErrors.website = 'Please enter a valid URL'
      }

      setErrors(nextErrors)
      if (Object.keys(nextErrors).length > 0) return

      const hmacUrl = getSubmissionHmacApiUrl()
      const submissionUrl = getSubmissionApiUrl()
      if (!hmacUrl || !submissionUrl) {
        setSubmitError('Signup API URL is not configured.')
        setStatus('error')
        return
      }

      const payload: SignupPayload = {
        dealership: dealership.trim(),
        firstName: firstName.trim(),
        lastName: lastName.trim(),
        email: email.trim(),
        verificationCode: 27,
      }
      if (websiteVal) {
        payload.website = websiteVal.startsWith('http') ? websiteVal : `https://${websiteVal}`
      }
      if (mobile.trim()) payload.mobile = '+61' + mobile.trim()
      if (clickedButtons.length > 0) payload.clickedButtons = [...clickedButtons]
      payload.interestLevelLabel = interestLevelLabel
      payload.interestOptions = {
        sendVideosAndInfo: interestOptionsArray[0],
        signUpNewsletter: interestOptionsArray[1],
        createAccount: interestOptionsArray[2],
        scheduleDemo: interestOptionsArray[3],
        trainStaff: interestOptionsArray[4],
      }
      if (roleKeyProp) payload.roleKey = roleKeyProp
      if (topicKeyProp) payload.topicKey = topicKeyProp

      // -- [Attribution: referrer, initial landing URL, UTM params, optional IP]
      const attribution = await getAttribution(3000)
      if (Object.keys(attribution).length > 0) payload.attribution = attribution

      setStatus('submitting')
      setSubmitError(null)
      setSubmitProgress(0)

      type ParsedError =
        | { kind: 'known'; summary: React.ReactNode; fieldErrors: Record<string, string> }
        | { kind: 'fallback'; message: string }

      async function parseErrorResponse(res: Response): Promise<ParsedError> {
        const text = await res.text()
        try {
          const j = JSON.parse(text) as {
            message?: string
            errors?: {
              exception?: {
                message?: string
                errors?: Record<string, unknown>
              }
            }
          }
          const exception = j.errors?.exception
          const msg = typeof exception?.message === 'string' ? exception.message : undefined
          const errObj = exception?.errors
          if (msg && errObj && typeof errObj === 'object' && !Array.isArray(errObj)) {
            const fieldErrors: Record<string, string> = {}
            for (const [field, list] of Object.entries(errObj)) {
              const arr = Array.isArray(list) ? list : [list]
              const first = arr.find((v): v is string => typeof v === 'string')
              if (first) fieldErrors[field] = first
            }
            if (Object.keys(fieldErrors).length > 0) {
              const fieldNames = Object.keys(fieldErrors)
              return {
                kind: 'known',
                summary: (
                  <>
                    {msg} — There are errors with the field(s){' '}
                    {fieldNames.map((f, i) => (
                      <React.Fragment key={f}>
                        {i > 0 ? ', ' : null}
                        <strong>{f}</strong>
                      </React.Fragment>
                    ))}
                    .
                  </>
                ),
                fieldErrors,
              }
            }
          }
          // -- [Exception with message but no field errors, e.g. "You already have a pending submission..."]
          if (msg) return { kind: 'fallback', message: msg }
          if (typeof j.message === 'string') return { kind: 'fallback', message: j.message }
          if (j.errors && typeof j.errors === 'object') {
            const first = Object.values(j.errors).flat()
            const msg = first.find((v) => typeof v === 'string')
            if (typeof msg === 'string') return { kind: 'fallback', message: msg }
          }
        } catch {
          // use text
        }
        return { kind: 'fallback', message: text || `Request failed: ${res.status}` }
      }

      try {
        const hmacRes = await fetch(hmacUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        })
        if (!hmacRes.ok) {
          const parsed = await parseErrorResponse(hmacRes)
          if (parsed.kind === 'known') {
            setSubmitError(parsed.summary)
            setErrors(parsed.fieldErrors)
          } else {
            setSubmitError(parsed.message)
            setErrors({})
          }
          setStatus('error')
          setSubmitProgress(0)
          return
        }
        const hmacBody = (await hmacRes.json()) as SignupHmacResponse
        const { hmac, ttl } = hmacBody
        if (typeof hmac !== 'string' || typeof ttl !== 'number') {
          setSubmitError('Invalid response from server. Please try again.')
          setStatus('error')
          setSubmitProgress(0)
          return
        }
        setSubmitProgress(50)

        const submissionPayload: SignupSubmissionPayload = { ...payload, hmac, ttl }
        const subRes = await fetch(submissionUrl, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(submissionPayload),
        })
        if (!subRes.ok) {
          const parsed = await parseErrorResponse(subRes)
          if (parsed.kind === 'known') {
            setSubmitError(parsed.summary)
            setErrors(parsed.fieldErrors)
          } else {
            setSubmitError(parsed.message)
            setErrors({})
          }
          setStatus('error')
          return
        }
        setSubmitProgress(100)
        didSuccessRef.current = true
        setStatus('success')
        setSubmittedDemoEmail(payload.email)
        setLastSubmission({
          dealership: payload.dealership,
          firstName: payload.firstName,
          lastName: payload.lastName,
          email: payload.email,
          mobile: mobile.trim(),
          interestLevelLabel,
          interestOptions: interestOptionsArray,
        })
        onSuccess?.()
        window.umami?.track('Demo signup submitted', { dealership: payload.dealership })
      } catch (err) {
        setStatus('error')
        setSubmitProgress(0)
        setSubmitError(err instanceof Error ? err.message : 'Something went wrong. Please try again.')
      }
    },
    [
      companyWebsiteConfirm,
      dealership,
      website,
      firstName,
      lastName,
      email,
      mobile,
      clickedButtons,
      interestLevelLabel,
      interestOptions,
      roleKeyProp,
      topicKeyProp,
      onSuccess,
    ]
  )

  const showForm = status !== 'success'

  return (
    <Dialog.Root open={open} onOpenChange={handleOpenChange}>
      <Dialog.Portal>
        <Dialog.Overlay className="fixed inset-0 bg-black/50 z-40" />
        <Dialog.Content
          className="fixed left-1/2 top-1/2 z-50 w-full max-w-lg -translate-x-1/2 -translate-y-1/2 rounded-lg bg-white p-6 shadow-xl focus:outline-none max-h-[90vh] overflow-y-auto"
          aria-describedby="signup-description"
        >
          <Dialog.Title className="sr-only">Book a demo – sign up</Dialog.Title>
          <p id="signup-description" className="text-gray-700 text-sm mb-6">
            {SIGNUP_INTRO}
          </p>

          {showForm ? (
            <form onSubmit={handleSubmit} className="space-y-4">
              {/* -- [Location permission notice: above Dealership; flash + pulse then green/red and slide away] */}
              {(locationNoticeState !== 'off' || locationNoticeExiting) && (
                <div
                  role="alert"
                  aria-live="polite"
                  className={`overflow-hidden rounded-lg border px-3 py-2.5 text-sm transition-all duration-300 ease-out ${
                    locationNoticeExiting === 'up'
                      ? 'translate-y-[-120%] opacity-0'
                      : locationNoticeExiting === 'right'
                        ? 'translate-x-[120%] opacity-0'
                        : 'translate-y-0 translate-x-0'
                  } ${
                    locationNoticeState === 'granted'
                      ? 'border-green-600 bg-green-50 text-green-900'
                      : locationNoticeState === 'denied'
                        ? 'border-red-700 bg-red-50 text-red-900'
                        : 'border-brand-orange/80 bg-[length:200%_100%] text-gray-900 animate-location-notice-flash animate-location-notice-pulse'
                  }`}
                  style={
                    locationNoticeState === 'asking'
                      ? { backgroundImage: 'linear-gradient(90deg, #ff7a3b, #ffbc48, #ff7a3b)' }
                      : undefined
                  }
                >
                  {locationNoticeState === 'granted' && (
                    <p className="font-medium">Thanks for allowing location — we’ll sort dealerships by distance.</p>
                  )}
                  {locationNoticeState === 'denied' && (
                    <p className="font-medium">
                      We won’t be able to order results by distance, but you can still choose a dealership below.
                    </p>
                  )}
                  {locationNoticeState === 'asking' && (
                    <p className="font-medium">{LOCATION_ASK_MESSAGE}</p>
                  )}
                </div>
              )}

              <div className="relative">
                <label htmlFor="signup-dealership" className="block text-sm font-medium text-gray-700 mb-1 cursor-pointer">
                  Dealership (required)
                </label>
                <input
                  ref={dealershipInputRef}
                  id="signup-dealership"
                  type="text"
                  value={dealership}
                  onChange={(e) => setDealership(e.target.value)}
                  onFocus={openDealershipSuggestions}
                  onBlur={closeDealershipSuggestions}
                  className={`w-full rounded border px-3 py-2 text-gray-900 focus:border-brand-orange focus:outline-none focus:ring-1 focus:ring-brand-orange ${errors.dealership ? 'border-red-600' : 'border-gray-300'}`}
                  placeholder="Dealership name"
                  autoComplete="organization"
                  aria-autocomplete="list"
                  aria-expanded={showDealershipList}
                  aria-controls="signup-dealership-list"
                  role="combobox"
                />
                {showDealershipList && (
                  <ul
                    ref={dealershipListRef}
                    id="signup-dealership-list"
                    role="listbox"
                    className="absolute z-10 mt-1 w-full max-h-56 overflow-auto rounded border border-gray-200 bg-white shadow-lg py-1"
                  >
                    {dealershipSuggestions.map((d, i) => (
                      <li
                        key={`${d.name}-${i}`}
                        role="option"
                        className="px-3 py-2 cursor-pointer hover:bg-gray-50 focus:bg-gray-50 focus:outline-none"
                        onMouseDown={(e) => {
                          e.preventDefault()
                          selectDealership(d.name, d.url)
                        }}
                      >
                        <span className="block font-medium text-gray-900">{d.name}</span>
                        {d.state && (
                          <span className="block text-xs text-gray-500 mt-0.5">{d.state}</span>
                        )}
                        {d.distanceKm != null && (
                          <span
                            className={`block text-xs mt-0.5 font-medium ${
                              d.distanceKm > 200 ? 'text-amber-600' : 'text-green-600'
                            }`}
                          >
                            {d.distanceKm > 200
                              ? `~${Math.round(d.distanceKm / 10) * 10} km away (approximate)`
                              : `~${Math.round(d.distanceKm / 5) * 5} km away (approximate)`}
                          </span>
                        )}
                      </li>
                    ))}
                  </ul>
                )}
                {errors.dealership && (
                  <p className="mt-1 text-sm text-red-600" role="alert">{errors.dealership}</p>
                )}
              </div>

              <div>
                <label htmlFor="signup-website" className="block text-sm font-medium text-gray-700 mb-1 cursor-pointer">
                  Website
                </label>
                <input
                  id="signup-website"
                  type="text"
                  value={website}
                  onChange={(e) => setWebsite(e.target.value)}
                  className={`w-full rounded border px-3 py-2 text-gray-900 focus:border-brand-orange focus:outline-none focus:ring-1 focus:ring-brand-orange ${errors.website ? 'border-red-600' : 'border-gray-300'}`}
                  placeholder="https://yoursite.com (optional)"
                  autoComplete="url"
                />
                {errors.website && (
                  <p className="mt-1 text-sm text-red-600" role="alert">{errors.website}</p>
                )}
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label htmlFor="signup-firstName" className="block text-sm font-medium text-gray-700 mb-1 cursor-pointer">
                    First Name (required)
                  </label>
                  <input
                    id="signup-firstName"
                    type="text"
                    value={firstName}
                    onChange={(e) => setFirstName(e.target.value)}
                    className={`w-full rounded border px-3 py-2 text-gray-900 focus:border-brand-orange focus:outline-none focus:ring-1 focus:ring-brand-orange ${errors.firstName ? 'border-red-600' : 'border-gray-300'}`}
                    placeholder="First name"
                    autoComplete="given-name"
                  />
                  {errors.firstName && (
                    <p className="mt-1 text-sm text-red-600" role="alert">{errors.firstName}</p>
                  )}
                </div>
                <div>
                  <label htmlFor="signup-lastName" className="block text-sm font-medium text-gray-700 mb-1 cursor-pointer">
                    Last Name (required)
                  </label>
                  <input
                    id="signup-lastName"
                    type="text"
                    value={lastName}
                    onChange={(e) => setLastName(e.target.value)}
                    className={`w-full rounded border px-3 py-2 text-gray-900 focus:border-brand-orange focus:outline-none focus:ring-1 focus:ring-brand-orange ${errors.lastName ? 'border-red-600' : 'border-gray-300'}`}
                    placeholder="Last name"
                    autoComplete="family-name"
                  />
                  {errors.lastName && (
                    <p className="mt-1 text-sm text-red-600" role="alert">{errors.lastName}</p>
                  )}
                </div>
              </div>

              <div>
                <label htmlFor="signup-email" className="block text-sm font-medium text-gray-700 mb-1 cursor-pointer">
                  Email (required)
                </label>
                {suggestedEmailDomain ? (
                  <div className={`flex items-center gap-1 rounded border bg-white focus-within:ring-1 focus-within:ring-brand-orange ${errors.email ? 'border-red-600' : 'border-gray-300 focus-within:border-brand-orange'}`}>
                    <input
                      id="signup-email"
                      type="text"
                      value={
                        email.includes('@')
                          ? email.slice(0, email.lastIndexOf('@'))
                          : email
                      }
                      onChange={(e) => {
                        const raw = e.target.value
                        // -- [Paste/autofill full email into local field: split and move domain to domain part]
                        if (raw.includes('@')) {
                          const atIdx = raw.indexOf('@')
                          const localPart = raw.slice(0, atIdx).trim()
                          const domainPart = raw.slice(atIdx + 1).replace(/^@+/, '').trim()
                          setEmail(domainPart ? localPart + '@' + domainPart : localPart)
                          return
                        }
                        const domain =
                          email.includes('@') ? email.slice(email.lastIndexOf('@') + 1) : suggestedEmailDomain
                        const domainClean = (domain ?? '').replace(/^@+/, '').trim()
                        setEmail(domainClean ? raw + '@' + domainClean : raw)
                      }}
                      className="flex-1 min-w-0 rounded border-0 px-3 py-2 text-gray-900 focus:outline-none"
                      placeholder="yourname"
                      autoComplete="username"
                    />
                    <span className="shrink-0 text-gray-500" aria-hidden>@</span>
                    <input
                      type="text"
                      aria-label="Email domain"
                      value={
                        email.includes('@')
                          ? email.slice(email.lastIndexOf('@') + 1)
                          : suggestedEmailDomain
                      }
                      onChange={(e) => {
                        const domain = e.target.value.replace(/^@+/, '').trim()
                        const local = email.includes('@')
                          ? email.slice(0, email.lastIndexOf('@'))
                          : email
                        setEmail(domain ? local + '@' + domain : local)
                      }}
                      className="min-w-[8rem] flex-1 rounded border-0 px-3 py-2 text-gray-900 focus:outline-none"
                      placeholder="domain.com"
                      autoComplete="email"
                    />
                  </div>
                ) : (
                  <input
                    id="signup-email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className={`w-full rounded border px-3 py-2 text-gray-900 focus:border-brand-orange focus:outline-none focus:ring-1 focus:ring-brand-orange ${errors.email ? 'border-red-600' : 'border-gray-300'}`}
                    placeholder="email@example.com"
                    autoComplete="email"
                  />
                )}
                {errors.email && (
                  <p className="mt-1 text-sm text-red-600" role="alert">{errors.email}</p>
                )}
              </div>

              <div>
                <label htmlFor="signup-mobile" className="block text-sm font-medium text-gray-700 mb-1 cursor-pointer">
                  Mobile
                </label>
                <div className={`flex items-center rounded border bg-white focus-within:ring-1 focus-within:ring-brand-orange ${errors.mobile ? 'border-red-600' : 'border-gray-300 focus-within:border-brand-orange'}`}>
                  <span
                    className="flex items-center gap-1.5 shrink-0 pl-3 text-gray-600 border-r border-gray-200 pr-2 py-2"
                    title="Australia"
                    aria-hidden
                  >
                    <span role="img" aria-label="Australia" title='Australia'>🇦🇺</span>
                    <span title='Australia'>+61</span>
                  </span>
                  <input
                    id="signup-mobile"
                    type="tel"
                    value={mobile}
                    onChange={(e) => setMobile(normalizeAustralianMobile(e.target.value))}
                    className={`flex-1 min-w-0 rounded-r border-0 px-3 py-2 text-gray-900 focus:outline-none ${errors.mobile ? 'bg-red-50/50' : ''}`}
                    placeholder="493088319"
                    autoComplete="tel"
                  />
                </div>
                {errors.mobile && (
                  <p className="mt-1 text-sm text-red-600" role="alert">{errors.mobile}</p>
                )}
              </div>

              {/* -- [Honeypot: hidden from users; bots that fill it are ignored; never sent to API] */}
              <div className="absolute -left-[9999px] w-0 h-0 overflow-hidden" aria-hidden>
                <label htmlFor="signup-company-website-confirm">Company website (confirm)</label>
                <input
                  id="signup-company-website-confirm"
                  type="text"
                  name="company_website_confirm"
                  tabIndex={-1}
                  autoComplete="off"
                  value={companyWebsiteConfirm}
                  onChange={(e) => setCompanyWebsiteConfirm(e.target.value)}
                />
              </div>

              <div className="space-y-3 pt-2 border-t border-gray-200">
                <label className="block text-sm font-medium text-gray-700 cursor-pointer">
                  How interested are you in Drivible?
                </label>
                <div className="flex items-center gap-2">
                  <div className="relative flex-1 flex items-center h-6">
                    <div
                      className="absolute inset-0 h-2 rounded-full pointer-events-none"
                      style={{
                        background: `linear-gradient(to right, rgb(234 88 12) 0%, rgb(234 88 12) ${(interestSliderValue / (INTEREST_LEVELS.length - 1)) * 100}%, rgb(209 213 219) ${(interestSliderValue / (INTEREST_LEVELS.length - 1)) * 100}%, rgb(209 213 219) 100%)`,
                      }}
                      aria-hidden
                    />
                    <input
                      type="range"
                      min={0}
                      max={INTEREST_LEVELS.length - 1}
                      step={1}
                      value={interestSliderValue}
                      onChange={(e) => setInterestFromSlider(Number(e.target.value))}
                      className="relative w-full h-2 rounded-full appearance-none bg-transparent accent-brand-orange cursor-pointer [&::-webkit-slider-runnable-track]:bg-transparent [&::-webkit-slider-runnable-track]:h-2 [&::-webkit-slider-runnable-track]:rounded-full [&::-moz-range-track]:bg-transparent [&::-moz-range-track]:h-2 [&::-moz-range-track]:rounded-full [&::-webkit-slider-thumb]:cursor-pointer [&::-moz-range-thumb]:cursor-pointer"
                      aria-valuemin={0}
                      aria-valuemax={INTEREST_LEVELS.length - 1}
                      aria-valuenow={interestSliderValue}
                      aria-valuetext={interestLevelLabel}
                    />
                  </div>
                  <span className="text-sm font-medium text-gray-700 min-w-[10rem] shrink-0 cursor-pointer">
                    {interestLevelLabel}
                  </span>
                </div>
                <div className="flex flex-col gap-2 pt-1">
                  {INTEREST_OPTION_LABELS.map((label, i) => (
                    <label
                      key={i}
                      className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer"
                    >
                      <input
                        type="checkbox"
                        checked={interestOptions[i]}
                        onChange={(e) => setInterestOption(i, e.target.checked)}
                        className="rounded border-gray-300 text-brand-orange focus:ring-brand-orange"
                      />
                      <span>{label}</span>
                    </label>
                  ))}
                </div>
              </div>

            {showForm && (interestOptions[2] || interestOptions[3] || interestOptions[4]) && (
              <p className="mt-4 text-xs text-gray-500">
                {DISCLAIMER}
              </p>
            )}

              {submitError && (
                <p className="text-sm text-red-600" role="alert">{submitError}</p>
              )}

              <button
                type="submit"
                disabled={status === 'submitting' || buttonErrorHighlight}
                className={`relative w-full rounded-lg border-2 px-4 py-3 font-semibold overflow-hidden cursor-pointer focus:outline-none focus:ring-2 focus:ring-offset-2 disabled:opacity-70 disabled:pointer-events-none transition-colors duration-[2000ms] ease-out ${
                  buttonErrorHighlight
                    ? 'border-red-600 bg-red-50 text-red-800 hover:bg-red-100 focus:ring-red-500'
                    : 'border-gray-900 bg-white text-gray-900 hover:bg-gray-50 focus:ring-brand-orange focus:ring-brand-orange'
                }`}
              >
                {/* -- [Progress bar across button; 0 → 50 after HMAC, 50 → 100 after submission] */}
                {status === 'submitting' && (
                  <span
                    className="absolute inset-y-0 left-0 bg-brand-orange/20 transition-all duration-300 ease-out"
                    style={{ width: `${submitProgress}%` }}
                    aria-hidden
                  />
                )}
                <span className="relative flex items-center justify-center gap-2">
                  {status === 'submitting' && (
                    <svg
                      className="animate-spin h-5 w-5 text-gray-600 shrink-0"
                      xmlns="http://www.w3.org/2000/svg"
                      fill="none"
                      viewBox="0 0 24 24"
                      aria-hidden
                    >
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                    </svg>
                  )}
                  {status === 'submitting' ? 'Submitting…' : buttonErrorHighlight ? 'Errored' : 'SUBMIT'}
                </span>
              </button>
            </form>
          ) : (
            <div className="rounded-lg border-2 border-green-600 bg-green-50 p-5 text-left">
              <div className="flex items-start gap-3">
                <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-green-600 text-white" aria-hidden>
                  <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                  </svg>
                </span>
                <div className="min-w-0 flex-1 space-y-3">
                  <p className="text-green-900 font-semibold">Thanks! We’ll be in touch shortly.</p>
                  <div className="rounded border border-green-200 bg-white/80 px-3 py-2.5 text-sm text-gray-800">
                    <p className="font-medium text-gray-900 mb-1.5">What you submitted:</p>
                    <ul className="space-y-1 text-gray-700">
                      <li><span className="text-gray-500">Dealership:</span> {dealership}</li>
                      <li><span className="text-gray-500">Name:</span> {firstName} {lastName}</li>
                      <li><span className="text-gray-500">Email:</span> {email}</li>
                      {mobile.trim() ? <li><span className="text-gray-500">Mobile:</span> +61{mobile}</li> : null}
                      <li><span className="text-gray-500">Interest:</span> {interestLevelLabel}</li>
                      {interestOptions.some(Boolean) && (
                        <li className="pt-1">
                          <span className="text-gray-500">Options:</span>
                          <ul className="mt-0.5 list-disc list-inside text-gray-700">
                            {INTEREST_OPTION_LABELS.filter((_, i) => interestOptions[i]).map((label, i) => (
                              <li key={i}>{label}</li>
                            ))}
                          </ul>
                        </li>
                      )}
                    </ul>
                  </div>
                  {(interestOptions[3] || interestOptions[4]) && (
                    <div className="rounded border border-amber-200 bg-amber-50 px-3 py-3 mt-3 text-sm">
                      <p className="text-amber-900 font-medium mb-1.5">Skip the wait</p>
                      <p className="text-gray-700 mb-2">Book a video call now and we’ll get you on the calendar right away.</p>
                      <a
                        href="https://cal.com/drivible-kublermdk/demo"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-center gap-1.5 font-semibold text-amber-800 hover:text-amber-900 underline focus:outline-none focus:ring-2 focus:ring-amber-500 focus:ring-offset-1 rounded"
                      >
                        Book a Video Call Now
                        <svg className="h-4 w-4 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                          <path strokeLinecap="round" strokeLinejoin="round" d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                        </svg>
                      </a>
                    </div>
                  )}
                  <div className="flex flex-wrap gap-2 mt-2">
                    <button
                      type="button"
                      onClick={handleSubmitAnother}
                      className="text-green-800 font-medium hover:underline cursor-pointer focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 rounded"
                    >
                      Submit Another
                    </button>
                    <button
                      type="button"
                      onClick={() => handleOpenChange(false)}
                      className="text-gray-800 font-medium hover:underline cursor-pointer focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 rounded"
                    >
                      Close
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}



          <Dialog.Close
            className="absolute right-4 top-4 rounded p-1 text-gray-500 hover:bg-gray-100 hover:text-gray-700 focus:outline-none focus:ring-2 focus:ring-brand-orange cursor-pointer"
            aria-label="Close"
          >
            <span aria-hidden>×</span>
          </Dialog.Close>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  )
}
